'use client';

import { useApi } from '@data-access';
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import { MixerHorizontalIcon } from '@radix-ui/react-icons';
import { Button } from '@registry/new-york/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@registry/new-york/ui/dropdown-menu';
import { viewSettingsStore } from '@stores';
import { Table } from '@tanstack/react-table';
import isEqual from 'lodash.isequal';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { COLUMN_VISIBILITY, RISK_PROFILE_TABLE_ID, RISK_TABLE_ID } from '../constants';

import styles from './DataTableViewOptions.module.scss';

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
  tableId: number;
  tableName: string;
  /** undefined renders inline; null waits for the slot to mount; an element portals into it. */
  container?: HTMLElement | null;
}

const DataTableViewOptionsComponent = <TData,>({
  table,
  tableId,
  tableName,
  container,
}: DataTableViewOptionsProps<TData>) => {
  const { api } = useApi();

  const storedColumnSettings = viewSettingsStore.viewSettingsById[tableId]?.columnSettings;

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>();

  const changeViewSettings = (value, column) => {
    table.getColumn(column).toggleVisibility(!!value);
    const columnSettings = { ...columnVisibility, ...{ [column]: value } };
    api.apiViewSettingsPartialUpdate({
      id: tableId,
      patchedViewSettings: { columnSettings },
    });
    viewSettingsStore.updateViewSettingsRecord(tableId, { columnSettings });
    setColumnVisibility(prevState => ({
      ...prevState,
      [column]: value,
    }));
  };

  const customFieldVisibility = Object.fromEntries(
    Object.entries(columnVisibility ?? {}).filter(([key]) => key.startsWith('customField_')),
  );

  const staticColumnVisibility = Object.fromEntries(
    Object.entries(columnVisibility ?? {}).filter(([key]) => !key.startsWith('customField_')),
  );

  const showResetToDefault = !isEqual(COLUMN_VISIBILITY[tableName], staticColumnVisibility);

  const resetToDefault = () => {
    // Set all columns in risk table to true, for others take values from initial value.
    // Always preserve custom field columns with their default visibility (true).
    const staticDefaults =
      tableName === RISK_TABLE_ID || tableName === RISK_PROFILE_TABLE_ID
        ? Object.fromEntries(
            table
              .getAllFlatColumns()
              .filter(column => column.id !== 'actions' && !column.id.startsWith('customField_'))
              .map(column => [column.id, true]),
          )
        : COLUMN_VISIBILITY[tableName];
    const customFieldDefaults = Object.fromEntries(Object.keys(customFieldVisibility).map(key => [key, true]));
    const allColumnVisibility = { ...staticDefaults, ...customFieldDefaults };
    table.setColumnVisibility({ ...allColumnVisibility });
    api.apiViewSettingsPartialUpdate({
      id: tableId,
      patchedViewSettings: { columnSettings: allColumnVisibility },
    });
    viewSettingsStore.updateViewSettingsRecord(tableId, { columnSettings: { ...allColumnVisibility } });
    delete allColumnVisibility.progress;
    setColumnVisibility(allColumnVisibility);
  };

  // Seed once from the store; the local copy diverges from the persisted one on purpose
  // (see the `progress` deletion above), so later store writes must not clobber it.
  useEffect(() => {
    if (!storedColumnSettings) return;
    setColumnVisibility(prevState => prevState ?? storedColumnSettings);
  }, [storedColumnSettings]);

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          // className="ml-auto mb-4 hidden h-8 lg:flex"
          className={styles.viewButton}
        >
          <MixerHorizontalIcon className={styles.icon} />
          View settings
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={styles.content}
        align="end"
      >
        <DropdownMenuLabel>Show columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className={styles.columns}>
          {table
            .getAllLeafColumns()
            .filter(column => typeof column.accessorFn !== 'undefined' && column.getCanHide())
            .map(column => {
              const getColumnLabel = (header, col, tableInstance) => {
                if (typeof header === 'function') {
                  const headerContext = { header, column: col, table: tableInstance };
                  const renderedHeader = header(headerContext);
                  return renderedHeader?.props?.title || '';
                }
                if (typeof header === 'string') {
                  return process.env.NODE_ENV === 'development' ? header : header.toLowerCase();
                }
                return '';
              };
              const label = getColumnLabel(column.columnDef.header, column, table);
              const value = columnVisibility?.[column.id] ?? true;
              return (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="capitalize"
                  checked={value}
                  onCheckedChange={val => changeViewSettings(val, column.id)}
                >
                  {label}
                </DropdownMenuCheckboxItem>
              );
            })}
        </div>
        {showResetToDefault && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem onClick={resetToDefault}>Reset to default</DropdownMenuCheckboxItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (container === undefined) return menu;
  return container ? createPortal(menu, container) : null;
};

// Cast keeps the generic signature, which `observer` would otherwise erase.
export const DataTableViewOptions = observer(DataTableViewOptionsComponent) as typeof DataTableViewOptionsComponent;
